import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Section } from "@/components/Section";
import { CheckCircle2, ArrowRight, Shield } from "lucide-react";

const CLINIC_TYPES = [
  "Primary Care",
  "Wellness Clinic",
  "Med Spa",
  "Concierge Practice",
  "Functional Medicine",
  "Other",
];

export default function RequestAccess() {
  const [form, setForm] = useState({
    clinic_name: "",
    contact_name: "",
    work_email: "",
    clinic_type: "",
    role: "",
    team_size: "",
    why: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  const handle = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.clinic_name || !form.contact_name || !form.work_email || !form.clinic_type) {
      toast.error("Please complete the required fields.");
      return;
    }
    try {
      setSubmitting(true);
      await api.post("/clinic-access-request", form);
      setSuccess(true);
      toast.success("Request received. Our team will reach out after review.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  if (success) {
    return (
      <Section className="pt-32" testId="request-access-success">
        <div className="max-w-xl mx-auto text-center">
          <div className="w-14 h-14 mx-auto rounded-full bg-emerald-50 border border-emerald-100 flex items-center justify-center">
            <CheckCircle2 className="w-6 h-6 text-emerald-600" />
          </div>
          <h1 className="mt-5 font-display text-3xl font-bold text-slate-900">Request received.</h1>
          <p className="mt-3 text-slate-600">
            Our partnerships team reviews each request against operational fit and program demand. You'll hear from us within 2 business days. If approved, we'll send a one-time temporary password to log in to the portal.
          </p>
          <Link
            to="/"
            className="mt-6 inline-flex items-center gap-1.5 text-sm font-semibold text-slate-900 hover:text-blue-700"
          >
            ← Back to home
          </Link>
        </div>
      </Section>
    );
  }

  return (
    <div data-testid="request-access-page">
      <Section className="pt-28">
        <div className="grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <div className="text-xs uppercase tracking-[0.22em] font-semibold text-blue-600 mb-4">Request portal access</div>
            <h1 className="font-display text-4xl sm:text-5xl font-extrabold tracking-tighter text-slate-900 leading-[1.02]">
              Apply for clinic portal access.
            </h1>
            <p className="mt-5 text-slate-600 leading-relaxed">
              The Nodera clinic portal is invite-only. Submit your details and our partnerships team will vet your clinic for activation. If approved, you'll receive credentials to log in.
            </p>
            <div className="mt-8 rounded-2xl border border-slate-200 bg-slate-50/60 p-5 flex items-start gap-3">
              <Shield className="w-4 h-4 text-slate-500 shrink-0 mt-0.5" />
              <p className="text-sm text-slate-600 leading-relaxed">
                Approval is based on operational fit and compliance posture. Approved clinics get access to the curated program catalog, vendor coordination workflows, and activation support.
              </p>
            </div>
          </div>
          <div className="lg:col-span-7">
            <form
              onSubmit={submit}
              data-testid="request-access-form"
              className="rounded-2xl border border-slate-200 bg-white p-6 lg:p-8 space-y-5 shadow-sm"
            >
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Clinic name" required>
                  <input required data-testid="request-access-clinic-input" value={form.clinic_name} onChange={handle("clinic_name")} className={inputCls} />
                </Field>
                <Field label="Clinic type" required>
                  <select required data-testid="request-access-type-select" value={form.clinic_type} onChange={handle("clinic_type")} className={inputCls}>
                    <option value="">Select a type</option>
                    {CLINIC_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                  </select>
                </Field>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Your name" required>
                  <input required data-testid="request-access-name-input" value={form.contact_name} onChange={handle("contact_name")} className={inputCls} />
                </Field>
                <Field label="Work email" required>
                  <input type="email" required data-testid="request-access-email-input" value={form.work_email} onChange={handle("work_email")} className={inputCls} />
                </Field>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Your role">
                  <input data-testid="request-access-role-input" value={form.role} onChange={handle("role")} className={inputCls} placeholder="e.g. Clinic Owner" />
                </Field>
                <Field label="Team size">
                  <select data-testid="request-access-team-size-select" value={form.team_size} onChange={handle("team_size")} className={inputCls}>
                    <option value="">Select</option>
                    <option>1–5</option>
                    <option>6–20</option>
                    <option>21–50</option>
                    <option>51+</option>
                  </select>
                </Field>
              </div>
              <Field label="What programs are you considering?">
                <textarea
                  data-testid="request-access-why-input"
                  value={form.why}
                  onChange={handle("why")}
                  rows={4}
                  className={`${inputCls} resize-y`}
                  placeholder="Briefly: programs you're exploring, current cash-pay setup, or specific operational pain points."
                />
              </Field>
              <button
                type="submit"
                disabled={submitting}
                data-testid="request-access-submit-btn"
                className="w-full inline-flex items-center justify-center gap-2 bg-slate-900 text-white rounded-full px-6 py-3.5 text-sm font-semibold hover:bg-slate-800 transition-colors disabled:opacity-50"
              >
                {submitting ? "Submitting…" : (<>Submit request <ArrowRight className="w-4 h-4" /></>)}
              </button>
              <p className="text-[11px] text-slate-500 text-center">
                By submitting, you agree we may contact you about Nodera Health portal access.
              </p>
            </form>
          </div>
        </div>
      </Section>
    </div>
  );
}

const inputCls =
  "w-full bg-white border border-slate-200 rounded-xl px-4 py-3 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-400 transition-colors";

function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="block text-xs font-semibold text-slate-700 mb-1.5">
        {label} {required && <span className="text-blue-600">*</span>}
      </span>
      {children}
    </label>
  );
}
